import EventEmitter from 'src/helpers/EventEmitter'
import AudioTrack from './tracks/AudioTrack'
import WebAudioTrack from './tracks/WebAudioTrack'
import VideoTrack from './tracks/VideoTrack'
import HlsVideoTrack from './tracks/HlsVideoTrack'
import BandwidthService from 'src/services/BandwidthService'
import { TweenLite } from 'gsap'
import { clamp } from 'src/helpers/MathUtils'
import Raf from 'src/helpers/Raf'

// HLS
import Hls from 'hls.js/dist/hls.light.min'
const hlsSupported = Hls.isSupported()

// CONSTANTS
const webAudioSupported = (window.AudioContext || window.webkitAudioContext) !== null
const isIOS = /(iOS|iPod|iPad|iPhone)/i.test(navigator.userAgent)

const STATES = {
  UNSTARTED: 'UNSTARTED',
  LOADING: 'LOADING',
  LOADED: 'LOADED',

  READY: 'READY',

  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  BUFFERING: 'BUFFERING',
  ENDED: 'ENDED',
}

const EVENTS = {
  UNSTARTED: 'UNSTARTED',
  LOADING: 'LOADING',
  LOADED: 'LOADED',

  READY: 'READY',

  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  BUFFERING: 'BUFFERING',
  ENDED: 'ENDED',
  TIME_UPDATE: 'TIME_UPDATE',

  STATE: 'state',
}

const stubVideoTrackConfig = {
  type: 'video',
  src: [
    {
      url: null,
      bitrate: 500,
    },
  ],
  canvasReady: false,
  preload: 'buffer',
  //loading.progress
}

function arraymove(arr, fromIndex, toIndex) {
  var element = arr[fromIndex]
  arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, element)
}

// MEDIA
class Media {
  get EVENTS() {
    return EVENTS
  }
  get STATES() {
    return STATES
  }

  constructor(config) {
    Object.assign(this, EventEmitter)

    // OBJECTS
    this.id = config.id
    this.tracks = []
    this.videoTrack = null
    this.audioTracks = []

    this.state = STATES.UNSTARTED

    // STUB VIDEO TRACK
    let videoTrackConfig = config.tracks.filter((track) => track.type === 'video')[0]
    if (!videoTrackConfig) {
      config.tracks.push(stubVideoTrackConfig)
    }

    // CREATE TRACKS
    for (let trackConfig of config.tracks) {
      let track = ''
      switch (trackConfig.type) {
        case 'video': {
          // Hls
          let useHls = true
          if (!hlsSupported) useHls = false
          if (isIOS && trackConfig.canvasReady) useHls = false
          if (!trackConfig.src.filter((src) => src.hls)[0]) useHls = false

          if (useHls) {
            track = new HlsVideoTrack(trackConfig)
          } else {
            // Select track by bitrate
            this._orderTrackByBitrate(trackConfig, BandwidthService.kbps)

            // Init track
            track = new VideoTrack(trackConfig)
          }
          this.videoTrack = track
          break
        }

        case 'audio': {
          if (webAudioSupported) {
            track = new WebAudioTrack(trackConfig)
          } else {
            track = new AudioTrack(trackConfig)
          }
          this.audioTracks.push(track)
          break
        }
      }

      if (track) this.tracks.push(track)
    }

    // STATE
    this.isMuted = false
    this.shouldLoop = config.loop || false
    this.currAudioTrack = this.audioTracks[0]

    // EVENTS
    this.videoTrack.el.addEventListener('ended', this.calculateState)
    this.videoTrack.el.addEventListener('pause', this.calculateState)
    //this.videoTrack.el.addEventListener('playing', this.calculateState)
    //this.videoTrack.el.addEventListener('waiting', this.calculateState)
    this.videoTrack.el.addEventListener('readyState', this.calculateState)
    this.videoTrack.el.addEventListener('timeupdate', this.calculateState)
    this.videoTrack.on(this.videoTrack.EVENTS.STATE, this.calculateState)
  }

  calculateState = () => {
    //todo move this down to MediaElementTrack
    // let v = this.videoTrack.el
    let s = this.videoTrack
    let ended = this.ended
    let paused = this.paused
    let playing = this.playing
    let buffering = this._buffering
    let unstarted = s.STATE.INIT === s.state //this.videoLoadProgress
    let loading = s.STATE.LOADING === s.state
    let loaded = s.STATE.LOADED === s.state

    let state = null
    if (unstarted) {
      state = STATES.UNSTARTED
    }
    if (loading) {
      state = STATES.LOADING
    }
    if (loaded) {
      state = STATES.LOADED
    }
    if (buffering) {
      state = STATES.BUFFERING
    }
    if (paused) {
      state = STATES.PAUSED
    }
    if (playing) {
      state = STATES.PLAYING
    }
    if (ended) {
      state = STATES.ENDED
    }

    this.setState(state)
  }

  setState(state) {
    let changed = this.state !== state
    this.state = state
    if (changed) {
      this.emit(EVENTS.STATE, this.state)
      // console.log('state:', state, this.readyState)
    }
  }

  _orderTrackByBitrate = (config, targetKbps) => {
    config.src.sort((a, b) => {
      return a.size - b.size
    })

    for (let idx in config.src) {
      let src = config.src[idx]
      if (src.hls) continue
      if (!src.size) continue
      let requiredKbps = (src.size * 8) / config.duration
      if (requiredKbps < targetKbps) {
        arraymove(config.src, idx, 0)
      }
    }
  }

  _onEnded = () => {
    this.emit(EVENTS.ENDED)
    if (this.shouldLoop) {
      this.currentTime = 0
      this.play()
    }
    // else {
    //   this.pause()
    // }
  }

  _onBuffering = () => {
    this.videoTrack.el.addEventListener('playing', this._onUnbuffering)
    for (let track of this.audioTracks) {
      track.pause()
    }
    this._buffering = true
    this.calculateState()
    this.emit(EVENTS.BUFFERING)
  }

  _onUnbuffering = () => {
    this.videoTrack.el.removeEventListener('playing', this._onUnbuffering)
    for (let track of this.audioTracks) {
      track.currentTime = this.videoTrack.currentTime
      track.play()
    }
    this._buffering = false
    this.calculateState()
    this.emit(EVENTS.UNBUFFERING)
  }

  _onTimeUpdate = () => {
    this.emit(EVENTS.TIME_UPDATE, { id: this.id, time: this.currentTime, duration: this.duration })
  }

  _onVisibilityChange = () => {
    if (document['hidden']) {
      this.pause()
    } else {
      this.play()
    }
  }

  // ************************************************************
  //
  // PUBLIC INTERFACE
  //
  // ************************************************************
  getDomElement = () => {
    return this.videoTrack.el
  }

  setSizeMode = (mode) => {
    let el = this.videoTrack.el
    el.style.display = 'block'
    switch (mode) {
      case 'cover': {
        el.style.width = `${window.innerWidth}px`
        el.style.height = `${window.innerHeight}px`
        el.style.objectFit = 'cover'
        break
      }
      case 'height': {
        let width = window.innerHeight * (el.videoWidth / el.videoHeight)
        el.style.width = `${width}px`
        el.style.height = `${window.innerHeight}px`
        el.style.objectFit = null
        break
      }
    }
  }

  load = () => {
    let p = []
    for (let track of this.tracks) {
      p.push(track.load())
    }
    return Promise.all(p)
  }

  play = () => {
    return new Promise((resolve) => {
      //restart
      if (this.ended) {
        this.currentTime = 0
      }

      for (let track of this.tracks) {
        // UNMUTE
        //if (!this.isMuted) {
        switch (track.config.type) {
          case 'video':
            if (this.audioTracks.length === 0) {
              track.muted = false
            }
            break
          case 'audio':
            track.muted = false
            track.volume = track === this.currAudioTrack ? this.volume : 0

            break
        }
        //}

        // ADD LISTENERS
        this.videoTrack.el.addEventListener('waiting', this._onBuffering)
        // document.addEventListener('visibilitychange', this._onVisibilityChange)
        this.videoTrack.el.addEventListener('ended', this._onEnded, { once: true })
        this.videoTrack.el.addEventListener('ended', resolve, { once: true })
        Raf.add(this._onTimeUpdate)

        // PLAY
        track.currentTime = this.videoTrack.currentTime
        track.play()
      }
    })
  }

  pause = () => {
    if (this.paused) return

    this.videoTrack.el.removeEventListener('waiting', this._onBuffering)
    // document.removeEventListener('visibilitychange', this._onVisibilityChange)
    this.videoTrack.el.removeEventListener('playing', this._onUnbuffering)
    this.videoTrack.el.removeEventListener('ended', this._onEnded)
    Raf.remove(this._onTimeUpdate)

    for (let track of this.tracks) {
      track.pause()
    }
  }

  setAudioTrackById = (id) => {
    let track = this.audioTracks.filter((track) => track.id === id)[0]
    this.setAudioTrack(track)
  }

  setAudioTrackByIndex = (i) => {
    this.setAudioTrack(this.audioTracks[i])
  }

  setAudioTrack(track) {
    if (!track || this.currAudioTrack === track) {
      return
    }
    this.currAudioTrack.volume = 0
    this.currAudioTrack = track
    this.currAudioTrack.volume = this.volume
  }

  blendAudioBetweenIndexes = (blend, index1, index2) => {
    this.currAudioTrack = this.audioTracks[index1]
    let a = this.audioTracks
    for (let i = 0, len = a.length; i < len; i++) {
      if (i === index1) {
        a[i].volume = (1 - blend) * this.volume
      } else if (i === index2) {
        a[i].volume = blend * this.volume
      } else {
        a[i].volume = 0
      }
    }
  }

  fadeToAudioTrackByIndex = (i, duration) => {
    duration = duration || 0
    let track = this.audioTracks[i]
    if (!track || this.currAudioTrack === track) {
      return
    }
    if (this.currAudioTrack === track) return

    if (duration === 0) {
      this.setAudioTrack(track)
    }

    TweenLite.killTweensOf([this.currAudioTrack, track])
    TweenLite.to(this.currAudioTrack, duration, { volume: 0 })
    this.currAudioTrack = track
    TweenLite.to(this.currAudioTrack, duration, { volume: this.volume })
  }

  get videoLoadProgress() {
    return this.videoTrack.loading.progress
  }

  get volume() {
    return this.videoTrack.volume
  }

  set volume(val) {
    let vol = clamp(val, 0, 1)
    this._volume = vol
    this.videoTrack.volume = vol
    TweenLite.killTweensOf(this.currAudioTrack)
    this.currAudioTrack.volume = vol
  }

  get muted() {
    return this.isMuted
  }

  set muted(val) {
    this.isMuted = val

    if (this.audioTracks.length > 0) {
      for (let track of this.audioTracks) {
        track.muted = val
      }
    } else {
      this.videoTrack.muted = val
    }
  }

  get progress() {
    return this.videoTrack.currentTime / this.videoTrack.duration
  }
  set progress(val) {
    this.currentTime = this.videoTrack.duration * val
  }

  get currentTime() {
    return this.videoTrack.currentTime
  }

  set currentTime(time) {
    for (let track of this.tracks) {
      track.currentTime = time
    }
  }

  get loop() {
    return this.shouldLoop
  }

  set loop(val) {
    this.shouldLoop = val
  }

  get duration() {
    return this.videoTrack.duration
  }

  get paused() {
    return this.videoTrack.paused
  }

  get readyState() {
    return this.videoTrack.el.readyState
  }

  get buffering() {
    return this.readyState < 3
  }

  get playing() {
    let v = this.videoTrack.el
    return v.currentTime > 0 && !v.paused && !v.ended && v.readyState > 2
  }

  get ended() {
    return this.videoTrack.ended
  }
}

Media.EVENTS = EVENTS

export default Media
